import {
  type ClientPolicy,
  type ClientPolicyAPI,
  type ClientPolicyPeriod,
  type ClientPolicyPeriodAPI,
  type GetClientPolicyResponse,
  type GetClientPolicyResponseAPI,
  PayTypeEnum,
} from '@omnipresentgroup/types';

import { getClientPolicyDescription } from './helpers';

const transformClientPolicyPeriodFromAPI = (
  clientPolicyPeriodAPI: ClientPolicyPeriodAPI,
): ClientPolicyPeriod => ({
  start: clientPolicyPeriodAPI.start,
  end: clientPolicyPeriodAPI.end,
  unit: clientPolicyPeriodAPI.unit,
  payDetails:
    clientPolicyPeriodAPI.pay_details.pay_type === PayTypeEnum.PERCENTAGE
      ? {
          payType: PayTypeEnum.PERCENTAGE,
          percentageValue: clientPolicyPeriodAPI.pay_details.percentage_value,
        }
      : {
          payType: PayTypeEnum.FIXED,
          fixedValue: clientPolicyPeriodAPI.pay_details.fixed_value,
        },
});

const transformClientPolicyPeriodToAPI = (
  clientPolicyPeriod: ClientPolicyPeriod,
): ClientPolicyPeriodAPI => ({
  start: clientPolicyPeriod.start,
  end: clientPolicyPeriod.end,
  unit: clientPolicyPeriod.unit,
  pay_details:
    clientPolicyPeriod.payDetails.payType === PayTypeEnum.PERCENTAGE
      ? {
          pay_type: PayTypeEnum.PERCENTAGE,
          percentage_value: clientPolicyPeriod.payDetails.percentageValue,
        }
      : {
          pay_type: PayTypeEnum.FIXED,
          fixed_value: clientPolicyPeriod.payDetails.fixedValue,
        },
});

const transformClientPolicyFromAPI = (
  clientPolicy: ClientPolicyAPI,
): ClientPolicy => {
  const periods = clientPolicy.periods.map((policyPeriod) =>
    transformClientPolicyPeriodFromAPI(policyPeriod),
  );

  return {
    type: clientPolicy.type,
    category: clientPolicy.category,
    country: clientPolicy.country,
    description: getClientPolicyDescription(periods),
    extraDescription: clientPolicy.extra_description,
    notice: clientPolicy.notice,
    periods,
    customMinimum: clientPolicy.custom_minimum
      ? transformClientPolicyPeriodFromAPI(clientPolicy.custom_minimum)
      : null,
    clientPolicyExplanations: clientPolicy.client_policy_explanations
      ? clientPolicy.client_policy_explanations.map((explanation) => ({
          title: explanation.title,
          descriptionItems: explanation.content,
        }))
      : undefined,
  };
};

export const transformClientPolicyToAPI = (
  clientPolicy: ClientPolicy,
): ClientPolicyAPI => ({
  type: clientPolicy.type,
  category: clientPolicy.category,
  country: clientPolicy.country,
  description: clientPolicy.description,
  extra_description: clientPolicy.extraDescription,
  notice: clientPolicy.notice,
  periods: clientPolicy.periods.map((policyPeriod) =>
    transformClientPolicyPeriodToAPI(policyPeriod),
  ),
  custom_minimum: clientPolicy.customMinimum
    ? transformClientPolicyPeriodToAPI(clientPolicy.customMinimum)
    : null,
});

export const transformGetClientPolicyResponseFromAPI = (
  items: GetClientPolicyResponseAPI,
): GetClientPolicyResponse =>
  items.map((item) => transformClientPolicyFromAPI(item));
